import styled from "styled-components";
import { Link } from "react-router-dom";
import { GlassCard } from "../../components/ui/Glass";

export const PageWrapper = styled.div``;

export const HeroSection = styled.section`
  background: ${({ $bg, theme }) => $bg || theme.colors.navy};
  color: ${({ theme }) => theme.colors.white};
  padding-top: calc(4rem + 60px);
  padding-bottom: ${({ theme }) => theme.spacing["3xl"]};
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-top: calc(4rem + 50px);
  }
`;

export const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing["3xl"]};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing["2xl"]};
  }
`;

export const HeroLeft = styled.div``;

export const Eyebrow = styled.p`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const BrandName = styled.h1`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(3rem, 7vw, ${({ theme }) => theme.fontSizes["7xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  line-height: 1;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.white};
`;

export const Tagline = styled.p`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.25rem, 2vw, ${({ theme }) => theme.fontSizes["2xl"]});
  font-style: italic;
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: rgba(255, 255, 255, 0.78);
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  max-width: 540px;
`;

export const BrandMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing["2xl"]};
`;

export const MetaItem = styled.div`
  span {
    display: block;
    font-family: ${({ theme }) => theme.fonts.caps};
    font-size: 10px;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 4px;
  }
  p {
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: ${({ theme }) => theme.fontSizes["2xl"]};
    font-weight: ${({ theme }) => theme.fontWeights.regular};
    color: ${({ theme }) => theme.colors.white};
  }
`;

export const HeroCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const PrimaryCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.navy};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }
`;

export const SecondaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.white};
    background: rgba(255, 255, 255, 0.08);
  }
`;

export const HeroVisual = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45);
  isolation: isolate;

  /* Direct child only — must not leak into the logo <img> inside HeroFooter */
  > img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ${({ theme }) => theme.transitions.fast};
  }

  &:hover > img {
    transform: scale(1.03);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      transparent 50%,
      rgba(0, 0, 0, 0.6) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
`;

export const HeroVisualOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  z-index: 1;
`;

export const HeroLogoMark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  backdrop-filter: blur(10px);
  padding: 8px;
  height: 45px;
  width: fit-content;

  svg {
    height: 100%;
    width: 100%;
    fill: #fff;
  }

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
    /* Force monochrome wordmark logos (PNG/colour) to white over the dark hero */
    filter: brightness(0) invert(1);
  }
`;

export const HeroFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: absolute;
  left: 16px;
  bottom: 16px;
  z-index: 2;
`;

export const HeroVisualCaption = styled.div`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
`;

export const HeroVisualPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.06),
    rgba(255, 255, 255, 0.02)
  );
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2rem, 5vw, ${({ theme }) => theme.fontSizes["5xl"]});
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
`;

export const Section = styled.section`
  padding: ${({ theme }) => theme.spacing["3xl"]} 0;
`;

export const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2rem, 4vw, ${({ theme }) => theme.fontSizes["4xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

export const Bio = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 760px;
  white-space: pre-line;
`;

export const Grid = styled.div`
  display: grid;
  /* minmax(0, 1fr) lets tracks shrink below content so the truncated cabins
     line clips with an ellipsis instead of widening the card. */
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const ClassBlock = styled.div`
  & + & {
    margin-top: ${({ theme }) => theme.spacing["3xl"]};
  }
`;

export const ClassHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

export const ClassTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.5rem, 3vw, ${({ theme }) => theme.fontSizes["3xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const ClassMeta = styled.span`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textLight};
`;

export const ModelCard = styled(GlassCard).attrs({ $variant: "light" })`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  box-shadow: ${({ theme }) => theme.shadows.md};
  height: 100%;
  display: flex;
  flex-direction: column;

  > div:last-child {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.white};
  }
`;

export const ModelTagRow = styled.div`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.md};
  left: ${({ theme }) => theme.spacing.md};
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  z-index: 1;
`;

export const ModelName = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes["2xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const ModelSubtype = styled.p`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const ModelSpecs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  margin-top: auto;

  /* Last column (Cabins) fills the row and truncates to a single line */
  > div:last-child {
    flex: 1;
    min-width: 0;

    p {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  > div {
    span {
      display: block;
      font-family: ${({ theme }) => theme.fonts.caps};
      font-size: 10px;
      letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.textLight};
      margin-bottom: 2px;
    }
    p {
      font-size: ${({ theme }) => theme.fontSizes.sm};
      font-weight: ${({ theme }) => theme.fontWeights.medium};
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  }
`;

export const Breadcrumbs = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  a {
    color: rgba(255, 255, 255, 0.7);
    transition: color ${({ theme }) => theme.transitions.fast};
    &:hover {
      color: ${({ theme }) => theme.colors.white};
    }
  }
`;

export const NotFound = styled.div`
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ theme }) => theme.spacing["2xl"]};
  color: ${({ theme }) => theme.colors.white};

  h2 {
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: ${({ theme }) => theme.fontSizes["3xl"]};
    font-weight: ${({ theme }) => theme.fontWeights.light};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
  p {
    color: rgba(255, 255, 255, 0.55);
    margin-bottom: ${({ theme }) => theme.spacing.xl};
  }
`;
